import { Buttons, CancelButton, DeleteButton, FormMessage, Icon, ModalCard, TextInput } from '~/components';
import { useApi, useSubscription, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import pluralize from 'pluralize';
import { colors } from '~/styles';

const P = styled.p`
  margin-bottom: 1rem;
`;

const WarningIcon = styled(Icon)`
  color: ${colors.warning};
`;

const ListItem = styled.li`
  list-style: initial;
  list-style-position: inside;
  padding-left: 0.6rem;
  font-weight: bold;
  color: ${colors.danger};
`;

function ProjectDeleteConfirmation({ project, onClose, onDelete }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, message, status }, form] = useForm();
  const [associations, setAssociations] = useState(null);
  const [text, setText] = useState('');
  const { notify } = useSubscription();

  useEffect(() => {
    (async () => {
      const { data: associations } = await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .associationTypesCount();

      setAssociations(associations);
    })();
  }, [api, project.id, workspace.id]);

  async function handleDelete() {
    try {
      form.submit();
      await api.www.workspaces(workspace.id).projects(project.id).delete();

      await onDelete(project);
    } catch ({ status, message }) {
      return form.error({ message });
    } finally {
      // Refresh the timer because deleting a client may delete its related time entry
      notify(useSubscription.keys.refresh_timer);
      notify(useSubscription.keys.refresh_time_approval_count);
      notify(useSubscription.keys.refresh_expense_approval_count);
    }
  }

  if (!associations) return null;

  const valid = !associations.hasDependencies || text === project.name;

  return (
    <ModalCard
      title={
        <>
          <WarningIcon icon="exclamation-triangle" /> Delete Project
        </>
      }
      onClose={onClose}>
      <ModalCard.Body>
        <P>
          This will delete the <strong>{project.name}</strong> project and all its related data including any tasks,
          time entries, expense items and resource allocations.
          {!associations.hasDependencies && ' Are you sure?'}
        </P>
        {associations.hasDependencies && (
          <>
            <P>Deleting this project will also permanently delete the following data from Ruddr:</P>
            <ul style={{ marginBottom: '1rem' }}>
              {Object.entries(associations.entities).map(([entity, count]) =>
                Number(count) > 0 ? (
                  <ListItem key={entity}>
                    {count} {pluralize(entity, count)}
                  </ListItem>
                ) : null,
              )}
            </ul>

            <FormMessage spaceBottom variant="danger">
              Deleting the <strong>{project.name}</strong> project will delete all of its data.{' '}
              <strong>This action cannot be undone.</strong>
            </FormMessage>
            <TextInput
              placeholder="Enter this project's name to confirm"
              value={text}
              onChange={({ target: { value } }) => setText(value)}
            />
          </>
        )}
        {status && <FormMessage.Error spaceTop>{message}</FormMessage.Error>}
      </ModalCard.Body>
      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <DeleteButton disabled={!valid} isLoading={isSubmitting} onClick={handleDelete}>
            Delete
          </DeleteButton>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}

export default ProjectDeleteConfirmation;
